// `detectMysqlWriteTargetSlot` — classifies the procedural slot at the
// cursor so the local-variable resolver can filter IN-mode parameters out of
// assignment-target positions.
//
// MySQL stored-routine parameters have a mode: IN (read-only), OUT
// (write-only after first assign), INOUT (read+write). At a slot that
// REQUIRES an assignable identifier (FETCH … INTO target, SET LHS,
// SELECT … INTO target), IN-mode parameters cannot legally appear. Surfacing
// them there confuses completion and produces unactionable suggestions.
//
// Pinned by goals/autocomplete-coverage/expected/mysql.ts —
// `unexpectedlyPresent: ['p_min_rentals']` (and equivalents) at script.33
// §11/§13, script.42 §5, script.45 §5, script.46 §8/§9.

import { tokenize } from '../context/tokenizer';

/** Write-target slot kinds — places where only assignable identifiers belong. */
export type MysqlWriteTargetSlot = 'fetch-into' | 'set-lhs' | 'select-into';

/**
 * Returns the write-target slot the cursor sits in, or null when the cursor
 * is in a read context (RHS of SET, after FROM in SELECT INTO, plain
 * expression context, etc).
 *
 * Algorithm: tokenize from buffer start to cursor, walk forward tracking the
 * current statement's structure. Statement boundaries are semicolons at
 * `BEGIN`-nesting depth 0 (or `BEGIN` / `THEN` / `ELSE` block-openers, which
 * are not themselves statements but reset the per-statement state).
 *
 * Per-statement state machine:
 *   - On `FETCH`        → expect `INTO`, then write-target until `;`.
 *   - On `SET`          → write-target until `=` or `;`.
 *   - On `SELECT`       → expect `INTO`, then write-target until `FROM`/`;`.
 *   - On `;` / block-opener → reset.
 *
 * Token tracking uses the existing `tokenize(source, ..., 'mysql')` lexer
 * (same one the in-buffer fallback uses), so the heuristic stays consistent
 * with the rest of the IntelliSense pipeline and tolerates comments/strings
 * transparently.
 */
export function detectMysqlWriteTargetSlot(
  source: string,
  cursorOffset: number,
): MysqlWriteTargetSlot | null {
  const tokens = tokenize(source, 0, cursorOffset, 'mysql');
  if (tokens.length === 0) return null;

  type StatementState =
    | { kind: 'none' }
    // Saw `FETCH`, waiting for `INTO`. Anything else still in the statement
    // is irrelevant for the slot classification.
    | { kind: 'fetch-pre-into' }
    // After `FETCH ... INTO`. Cursor sits in the comma-separated target
    // list. Stays until `;`.
    | { kind: 'fetch-into-target' }
    // After `SET`. Cursor is on the LHS until we see `=`, after which we
    // transition to read context for the RHS.
    | { kind: 'set-lhs' }
    // After SET LHS = … (read context). Stays until `;`.
    | { kind: 'set-rhs' }
    // Saw `SELECT`, waiting for `INTO` (or `FROM` which resets to read).
    | { kind: 'select-pre-into' }
    // After `SELECT … INTO`, cursor in target list. Reset on FROM/`;`.
    | { kind: 'select-into-target' }
    // After `SELECT … INTO … FROM …` (read context).
    | { kind: 'select-rhs' };

  let state: StatementState = { kind: 'none' };

  const resetStatement = () => {
    state = { kind: 'none' };
  };

  for (const tok of tokens) {
    if (tok.start >= cursorOffset) break;

    // Statement terminator — semicolon at any depth resets per-statement
    // state. Compound-statement keywords (BEGIN/THEN/ELSE/END) also reset
    // because they're block boundaries, not statements themselves.
    if (tok.type === 'semicolon') {
      resetStatement();
      continue;
    }
    if (tok.type === 'kw' || tok.type === 'ident') {
      const upper = tok.text.toUpperCase();
      if (
        upper === 'BEGIN' ||
        upper === 'THEN' ||
        upper === 'ELSE' ||
        upper === 'END' ||
        upper === 'LOOP' ||
        upper === 'DO'
      ) {
        resetStatement();
        continue;
      }
    }

    // State transitions on keywords/operators.
    if (tok.type === 'kw' || tok.type === 'ident') {
      const upper = tok.text.toUpperCase();
      switch (state.kind) {
        case 'none':
          if (upper === 'FETCH') {
            state = { kind: 'fetch-pre-into' };
          } else if (upper === 'SET') {
            state = { kind: 'set-lhs' };
          } else if (upper === 'SELECT') {
            state = { kind: 'select-pre-into' };
          }
          break;
        case 'fetch-pre-into':
          if (upper === 'INTO') state = { kind: 'fetch-into-target' };
          break;
        case 'select-pre-into':
          if (upper === 'INTO') {
            state = { kind: 'select-into-target' };
          } else if (upper === 'FROM') {
            state = { kind: 'select-rhs' };
          }
          break;
        case 'select-into-target':
          if (upper === 'FROM') state = { kind: 'select-rhs' };
          break;
        default:
          break;
      }
      continue;
    }

    // Assignment-operator transition: SET LHS → SET RHS at the first `=`.
    if (tok.type === 'op' && tok.text === '=' && state.kind === 'set-lhs') {
      state = { kind: 'set-rhs' };
      continue;
    }
  }

  switch (state.kind) {
    case 'fetch-into-target':
      return 'fetch-into';
    case 'set-lhs':
      return 'set-lhs';
    case 'select-into-target':
      return 'select-into';
    default:
      return null;
  }
}
